import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { CategoryManager } from "@/components/CategoryManager";
import { logAdminSignIn } from "@/lib/admin-auth.functions";
import { AdminNav } from "@/components/admin/AdminNav";

type Row = {
  id: string;
  title: string;
  slug: string;
  status: "draft" | "published";
  updated_at: string;
  published_at: string | null;
  category_id: string | null;
};
type Category = { id: string; name: string };

type SortKey = "updated_desc" | "updated_asc" | "published_desc" | "title_asc" | "title_desc";
type StatusFilter = "all" | "draft" | "published";
const PAGE_SIZE = 20;

export const Route = createFileRoute("/admin/")({
  head: () => ({
    meta: [
      { title: "Admin | Posts | Scott Cunningham" },
      { name: "description", content: "Posts for Scott Cunningham’s website administration." },
      { property: "og:title", content: "Admin | Posts | Scott Cunningham" },
      { property: "og:description", content: "Posts for Scott Cunningham’s website administration." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Admin | Posts | Scott Cunningham" },
      { name: "twitter:description", content: "Posts for Scott Cunningham’s website administration." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminHome,
});

function AdminHome() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [categoryId, setCategoryId] = useState<string>("all");
  const [sort, setSort] = useState<SortKey>("updated_desc");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate({ to: "/login" });
  }, [user, loading, navigate]);

  // Log a successful admin sign-in once per browser session.
  useEffect(() => {
    if (!isAdmin || !user) return;
    const key = `admin_signin_logged_${user.id}`;
    if (sessionStorage.getItem(key)) return;
    supabase.auth.getSession().then(({ data }) => {
      const token = data.session?.access_token;
      if (!token) return;
      logAdminSignIn({ data: { accessToken: token } })
        .then(() => sessionStorage.setItem(key, "1"))
        .catch(() => {});
    });
  }, [isAdmin, user]);

  useEffect(() => {
    if (!isAdmin) return;
    supabase
      .from("posts")
      .select("id, title, slug, status, updated_at, published_at, category_id")
      .order("updated_at", { ascending: false })
      .then(({ data }) => setRows((data as Row[]) ?? []));
    supabase
      .from("categories")
      .select("id, name")
      .order("name", { ascending: true })
      .then(({ data }) => setCategories((data as Category[]) ?? []));
  }, [isAdmin]);

  const remove = async (id: string) => {
    if (!confirm("Delete this post?")) return;
    const { error } = await supabase.from("posts").delete().eq("id", id);
    if (error) return alert(error.message);
    setRows((r) => r.filter((p) => p.id !== id));
  };

  const categoryName = (id: string | null) =>
    id ? (categories.find((c) => c.id === id)?.name ?? "N/A") : "N/A";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = rows.filter((p) => {
      if (status !== "all" && p.status !== status) return false;
      if (categoryId !== "all" && p.category_id !== categoryId) return false;
      if (q && !(p.title?.toLowerCase().includes(q) || p.slug?.toLowerCase().includes(q)))
        return false;
      return true;
    });
    const sorted = [...list].sort((a, b) => {
      switch (sort) {
        case "updated_asc":
          return a.updated_at.localeCompare(b.updated_at);
        case "updated_desc":
          return b.updated_at.localeCompare(a.updated_at);
        case "published_desc":
          return (b.published_at ?? "").localeCompare(a.published_at ?? "");
        case "title_asc":
          return (a.title || "").localeCompare(b.title || "");
        case "title_desc":
          return (b.title || "").localeCompare(a.title || "");
      }
    });
    return sorted;
  }, [rows, query, status, categoryId, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [query, status, categoryId, sort]);

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/login" });
  };

  if (loading) return <div className="p-12 text-muted-foreground">Loading…</div>;
  if (!user) return null;
  if (!isAdmin)
    return (
      <div className="mx-auto max-w-md px-6 py-24">
        <h1 className="font-display text-3xl">No admin access</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Your account ({user.email}) isn't an admin. Sign in with the first account that was
          created on this site, or grant admin in the database.
        </p>
        <Button className="mt-6" onClick={signOut}>
          Sign out
        </Button>
      </div>
    );

  return (
    <div className="mx-auto max-w-5xl px-6 py-16">
      <AdminNav
        title="Posts"
        actions={
          <>
            <Link to="/admin/posts/new">
              <Button>New post</Button>
            </Link>
            <Button variant="outline" onClick={signOut}>
              Sign out
            </Button>
          </>
        }
      />

      <div className="mt-8 grid gap-3 md:grid-cols-[1fr_auto_auto_auto]">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search title or slug…"
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          className="rounded-md border border-border bg-background px-3 py-2 text-sm"
          aria-label="Filter by status"
        >
          <option value="all">All statuses</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className="rounded-md border border-border bg-background px-3 py-2 text-sm"
          aria-label="Filter by category"
        >
          <option value="all">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          className="rounded-md border border-border bg-background px-3 py-2 text-sm"
          aria-label="Sort posts"
        >
          <option value="updated_desc">Recently updated</option>
          <option value="updated_asc">Least recently updated</option>
          <option value="published_desc">Recently published</option>
          <option value="title_asc">Title A–Z</option>
          <option value="title_desc">Title Z–A</option>
        </select>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {filtered.length} of {rows.length} {rows.length === 1 ? "post" : "posts"}
        </span>
        {(query || status !== "all" || categoryId !== "all") && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setStatus("all");
              setCategoryId("all");
            }}
            className="underline hover:text-foreground"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="mt-4 divide-y divide-border rounded-md border border-border">
        {filtered.length === 0 && (
          <p className="p-6 text-muted-foreground">
            {rows.length === 0
              ? "No posts yet. Create your first one."
              : "No posts match these filters."}
          </p>
        )}
        {pageRows.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-4 p-4">
            <div>
              <Link
                to="/admin/posts/$id"
                params={{ id: p.id }}
                className="font-display text-xl hover:underline"
              >
                {p.title || "(untitled)"}
              </Link>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                /{p.slug} · {p.status} · {categoryName(p.category_id)} ·{" "}
                {new Date(p.updated_at).toLocaleDateString()}
              </div>
            </div>
            <div className="flex gap-2">
              {p.status === "published" && (
                <Link to="/blog/$slug" params={{ slug: p.slug }}>
                  <Button variant="outline" size="sm">
                    View
                  </Button>
                </Link>
              )}
              <Link to="/admin/posts/$id" params={{ id: p.id }}>
                <Button variant="outline" size="sm">
                  Edit
                </Button>
              </Link>
              <Button variant="destructive" size="sm" onClick={() => remove(p.id)}>
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between gap-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={safePage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <span className="text-muted-foreground">
            Page {safePage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={safePage >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </div>
      )}

      <div className="mt-10">
        <CategoryManager />
      </div>
    </div>
  );
}
